import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import CompanyActivationPanel from './CompanyActivationPanel';
import AdviserObligationsPanel from './AdviserObligationsPanel';
import {
  adviserBillingApi,
  type AdviserBillingCard,
  type AdviserBillingComponent,
  type CompanyAdviserBilling,
} from '../../advisers/api';

const cardLabel = (card: AdviserBillingCard | null) =>
  card
    ? `${card.brand.toUpperCase()} •••• ${card.last4} · ${String(card.expMonth).padStart(2, '0')}/${card.expYear}`
    : 'No card selected';
const date = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : 'Awaiting billing confirmation';

function ComponentPaymentForm({
  companyId,
  practiceId,
  component,
  cards,
  onChanged,
}: {
  companyId: string;
  practiceId?: string;
  component: AdviserBillingComponent;
  cards: AdviserBillingCard[];
  onChanged: () => Promise<void>;
}) {
  const [cardId, setCardId] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const payerType = practiceId ? 'practice' : 'client';
  const samePayer =
    component.payerType === payerType && (!practiceId || component.payerPracticeId === practiceId);
  const pending = component.pendingHandover;
  return (
    <article className="adviser-billing-component">
      <div className="adviser-section-heading">
        <div>
          <h3>{component.component === 'planning' ? 'Company planning' : 'Pro execution'}</h3>
          <p>
            {component.quantity}{' '}
            {component.component === 'planning' ? 'company plan' : 'client seats'} ·{' '}
            {component.status}
          </p>
        </div>
        <span className="adviser-badge">
          {component.payerType === 'practice' ? 'Adviser-paid' : 'Client-paid'}
        </span>
      </div>
      <div className="adviser-billing-facts">
        <div>
          <span>Selected card</span>
          <strong>{cardLabel(component.selectedCard)}</strong>
        </div>
        <div>
          <span>Next renewal</span>
          <strong>{date(component.renewsAt)}</strong>
        </div>
      </div>
      {pending && (
        <div className="adviser-notice">
          <strong>
            {pending.targetPayerType === 'practice' ? 'Adviser' : 'Client'} payment change ·{' '}
            {pending.status}
          </strong>
          <p>Effective {date(pending.effectiveAt)} for this component.</p>
          {pending.error && <p role="alert">{pending.error}</p>}
        </div>
      )}
      {error && (
        <p className="adviser-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="adviser-success" role="status">
          {notice}
        </p>
      )}
      {component.status === 'incomplete' ? (
        <p className="adviser-notice">
          Complete the original payment invoice before changing this product’s payer or card.
        </p>
      ) : (
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setError('');
            setNotice('');
            try {
              await adviserBillingApi.payer(companyId, component.component, {
                payerType,
                ...(practiceId && { practiceId }),
                paymentMethodId: cardId,
                authoriseRecurring: true,
              });
              await onChanged();
              setConsent(false);
              setNotice(
                samePayer
                  ? 'The selected card for this subscription has been updated.'
                  : 'Payer change authorised for this component’s next renewal.'
              );
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Unable to update payment.');
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            {samePayer
              ? 'Change the selected card'
              : practiceId
                ? 'Authorise your firm to pay at the next renewal'
                : 'Authorise your company to pay at the next renewal'}
            <select required value={cardId} onChange={(event) => setCardId(event.target.value)}>
              <option value="">Choose an authorised payment method</option>
              {cards.map((card) => (
                <option key={card.id} value={card.id}>
                  {cardLabel(card)}
                </option>
              ))}
            </select>
          </label>
          <label className="adviser-consent">
            <input
              required
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
            />
            <span>
              I authorise recurring charges for this company’s{' '}
              {component.component === 'planning' ? 'planning subscription' : 'Pro subscription'} to
              the selected {practiceId ? 'practice' : 'company'} card.
            </span>
          </label>
          <button className="adviser-button" type="submit" disabled={busy || !cardId || !consent}>
            {busy
              ? 'Authorising…'
              : samePayer
                ? 'Save selected card'
                : practiceId
                  ? 'Switch to adviser-paid'
                  : 'Switch to client-paid'}
          </button>
        </form>
      )}
    </article>
  );
}
export function CompanyAdviserBillingPanel({
  companyId,
  practiceId,
}: {
  companyId: string;
  practiceId?: string;
}) {
  const [billing, setBilling] = useState<CompanyAdviserBilling | null>(null);
  const [cards, setCards] = useState<AdviserBillingCard[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const scope = practiceId ? { practiceId } : { companyId };
  const [pendingComponents, setPendingComponents] = useState<string[]>([]);
  const refresh = async () => {
    const next = await adviserBillingApi.company(companyId);
    setBilling(next);
    if (next.canManage && !next.isDemo) setCards(await adviserBillingApi.cards(scope));
  };
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    adviserBillingApi
      .company(companyId)
      .then(async (next) => {
        if (!active) return;
        setBilling(next);
        if (next.canManage && !next.isDemo) {
          const methods = await adviserBillingApi.cards(
            practiceId ? { practiceId } : { companyId }
          );
          if (active) setCards(methods);
        }
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId, practiceId]);
  if (loading)
    return (
      <p role="status" className="adviser-muted">
        Loading company billing…
      </p>
    );
  if (error)
    return (
      <p className="adviser-error" role="alert">
        {error}
      </p>
    );
  if (!billing) return null;
  if (billing.isDemo)
    return (
      <div className="adviser-notice">
        This private demo has demo entitlement. It has no paid subscription and does not consume
        client seats.
      </div>
    );
  return (
    <div className="adviser-billing-surface">
      {billing.personalPlanningCoverage && (
        <div className="adviser-notice">
          <strong>Planning is covered by a personal Founder plan.</strong>
          <p>
            The existing account subscription continues to provide coverage. Per-company payer
            changes do not transfer that personal subscription.
          </p>
          {!practiceId && (
            <Link className="adviser-text-link" to="/billing">
              Manage my personal billing
            </Link>
          )}
        </div>
      )}
      {billing.canManage && (
        <div className="adviser-billing-card-tools">
          <p className="adviser-muted">
            {practiceId
              ? 'Only your firm’s authorised cards appear here. The client authorises client-paid billing through their company administration.'
              : 'Only this company’s authorised cards appear here. The practice owner authorises adviser-paid billing through their practice workspace.'}
          </p>
          <button
            className="adviser-button adviser-button-secondary"
            type="button"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                const result = await adviserBillingApi.setup(scope);
                const redirect = new URL(result.url);
                if (redirect.protocol !== 'https:')
                  throw new Error('The payment setup link is unavailable.');
                window.location.assign(redirect.href);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Unable to add payment method.');
                setBusy(false);
              }
            }}
          >
            <CreditCard size={15} />
            {busy ? 'Opening secure setup…' : 'Add an authorised card'}
          </button>
        </div>
      )}
      {billing.canManage && (
        <CompanyActivationPanel
          companyId={companyId}
          practiceId={practiceId}
          cards={cards}
          onChanged={refresh}
          onPendingComponents={setPendingComponents}
        />
      )}
      {billing.components.length ? (
        billing.components
          .filter((component) => !pendingComponents.includes(component.component))
          .map((component) =>
            billing.canManage ? (
              <ComponentPaymentForm
                companyId={companyId}
                practiceId={practiceId}
                component={component}
                cards={cards}
                key={component.id}
                onChanged={refresh}
              />
            ) : (
              <div className="adviser-billing-component" key={component.id}>
                <h3>{component.component === 'planning' ? 'Company planning' : 'Pro execution'}</h3>
                <p>
                  {component.payerType === 'practice' ? 'Adviser-paid' : 'Client-paid'} ·{' '}
                  {component.status} · Renews {date(component.renewsAt)}
                </p>
              </div>
            )
          )
      ) : !billing.canManage ? (
        <div className="adviser-empty">
          <h3>No active company subscriptions.</h3>
          <p>
            {billing.personalPlanningCoverage
              ? 'Personal Founder coverage is available. The billing administrator can activate company Pro.'
              : 'The client billing administrator or practice owner can activate a company product.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
export default function AdviserBillingPanel({
  practiceId,
  clients,
}: {
  practiceId: string;
  clients: Array<{
    companyId: string;
    name?: string;
    companyName?: string;
    isDemo?: boolean;
    status?: string;
  }>;
}) {
  const actualClients = clients.filter((client) => !client.isDemo && client.status !== 'pending');
  const [selected, setSelected] = useState(actualClients[0]?.companyId || '');
  useEffect(() => {
    if (!actualClients.some((client) => client.companyId === selected))
      setSelected(actualClients[0]?.companyId || '');
  }, [clients, selected]);
  return (
    <section>
      <div className="adviser-section-heading">
        <div>
          <h2>Company billing</h2>
          <p>
            Choose the payer and card for each client company. Planning and Pro renew independently.
          </p>
        </div>
      </div>
      <div className="adviser-panel">
        <label>
          Client company
          <select value={selected} onChange={(event) => setSelected(event.target.value)}>
            {actualClients.map((client) => (
              <option key={client.companyId} value={client.companyId}>
                {client.name || client.companyName}
              </option>
            ))}
          </select>
        </label>
        <p className="adviser-muted">
          {clients.filter((client) => client.isDemo).length} private demos excluded from billing.
        </p>
      </div>
      {selected ? (
        <div className="adviser-panel">
          <CompanyAdviserBillingPanel key={selected} companyId={selected} practiceId={practiceId} />
        </div>
      ) : (
        <div className="adviser-empty">
          <h3>No linked client companies.</h3>
          <p>
            Create or link a client company to manage its billing. Demos have no paid subscriptions.
          </p>
        </div>
      )}
      <AdviserObligationsPanel practiceId={practiceId} />
    </section>
  );
}
